import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../context/auth.store';
import toast from 'react-hot-toast';

const DEMO_ACCOUNTS = [
  { label: '👑 Admin (Kaizen)', email: 'admin@kaizeninfoserve.com', password: 'Kaizen@1234' },
  { label: '👤 Naveen C (Branch)', email: 'naveen_c@kaizeninfoserve.com', password: 'Kaizen@1234' },
  { label: '🌐 Micron (Client)', email: 'pmunjal@micron.com', password: 'Client@1234' },
  { label: '🌐 Cooler Master (Client)', email: 'sanket_naik@coolermaster.com.tw', password: 'Client@1234' },
  { label: '🌐 Inno3D (Client)', email: 'stanley.chow@inno3d.com', password: 'Client@1234' },
  { label: '🌐 Galax (Client)', email: 'thomas_chiu@galax.com', password: 'Client@1234' },
];

const TF_PILLARS = [
  {
    icon: '🤖',
    title: 'AI-Driven Analytics',
    desc: 'Intelligent synthesis, anomaly detection, and automated report intelligence.',
    badge: 'AI',
    color: '#00C0FA',
  },
  {
    icon: '📊',
    title: 'Advanced Analytics',
    desc: 'Deep multi-department metrics across 110,000+ files and live storage footprints.',
    badge: 'ANALYTICS',
    color: '#3B82F6',
  },
  {
    icon: '⚡',
    title: 'Distribution Automation',
    desc: 'Scheduled multi-recipient email flows, automated dispatches, and delivery tracking.',
    badge: 'AUTOMATION',
    color: '#FF6B00',
  },
  {
    icon: '🛡️',
    title: 'Security Transformation',
    desc: 'Zero-trust governance, interactive bowtie access models, and full audit trails.',
    badge: 'TRANSFORMATION',
    color: '#10B981',
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  // Handle voice login events triggered by KYRO
  useEffect(() => {
    const handleVoiceLogin = async (event) => {
      const action = event.detail?.action;
      let targetEmail = '';
      let targetPassword = '';

      if (action === 'login_admin') {
        targetEmail = 'admin@kaizeninfoserve.com';
        targetPassword = 'Kaizen@1234';
      } else if (action === 'login_naveen') {
        targetEmail = 'naveen_c@kaizeninfoserve.com';
        targetPassword = 'Kaizen@1234';
      } else if (action === 'login_senthil') {
        targetEmail = 'senthil_k@kaizeninfoserve.com';
        targetPassword = 'Kaizen@1234';
      }

      if (targetEmail && targetPassword) {
        setEmail(targetEmail);
        setPassword(targetPassword);
        setError('');
        setLoading(true);
        toast.loading(`🎙️ KYRO Voice Login: Authenticating as ${targetEmail}...`, { id: 'voice-login' });
        try {
          await login(targetEmail, targetPassword);
          toast.success(`Welcome back! Authenticated via KYRO Voice.`, { id: 'voice-login' });
          navigate('/');
        } catch (err) {
          toast.error('Voice login failed. Please try again.', { id: 'voice-login' });
          setError(err.response?.data?.error || 'Authentication error');
        } finally {
          setLoading(false);
        }
      }
    };

    window.addEventListener('kyro-voice-login', handleVoiceLogin);
    return () => window.removeEventListener('kyro-voice-login', handleVoiceLogin);
  }, [login, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const cleanEmail = (email || '').trim().toLowerCase();
    const cleanPassword = (password || '').trim();
    try {
      await login(cleanEmail, cleanPassword);
      toast.success('Welcome to PX-Reports powered by Trendforge!');
      navigate('/');
    } catch (err) {
      if (err.message && (err.message.includes('Network Error') || err.code === 'ERR_NETWORK')) {
        setError('Connection error. Please refresh and check your internet connection.');
      } else {
        setError(err.response?.data?.error || 'Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (a) => {
    setEmail(a.email);
    setPassword(a.password);
    setError('');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #070B19 0%, #0F172A 50%, #0A1128 100%)',
        padding: '30px',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* Dynamic Ambient Background Glows */}
      <div
        style={{
          position: 'absolute',
          top: '-15%',
          left: '-10%',
          width: '550px',
          height: '550px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0, 192, 250, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-15%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 87, 34, 0.18) 0%, transparent 70%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
        }}
      />

      {/* Main Glassmorphic Modal Container */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.25fr 1fr',
          maxWidth: '1080px',
          width: '100%',
          borderRadius: '28px',
          background: 'rgba(15, 23, 42, 0.75)',
          border: '1.5px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 192, 250, 0.1)',
          backdropFilter: 'blur(24px)',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* ── LEFT SHOWCASE PANEL ── */}
        <div
          style={{
            padding: '48px 44px',
            background: 'linear-gradient(180deg, rgba(11, 19, 43, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)',
            borderRight: '1.5px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            {/* Trendforge Branding Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '18px', marginBottom: '24px' }}>
              <img
                src="/trendforge_logo.png"
                alt="Trendforge"
                style={{
                  width: '68px',
                  height: '68px',
                  borderRadius: '16px',
                  objectFit: 'contain',
                  background: '#FFFFFF',
                  padding: '5px',
                  boxShadow: '0 8px 24px rgba(0, 192, 250, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                }}
              />
              <div>
                <div
                  style={{
                    fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
                    fontWeight: '900',
                    fontSize: '22px',
                    letterSpacing: '0.08em',
                    color: '#FFFFFF',
                    lineHeight: 1.1,
                  }}
                >
                  PX <span style={{ color: '#00C0FA' }}>REPORTS</span>
                </div>
                
                {/* TREND FORGE LOGO TEXT */}
                <div
                  style={{
                    fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
                    fontSize: '15px',
                    fontWeight: '900',
                    letterSpacing: '0.12em',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span style={{ color: '#FFFFFF' }}>TREND</span>
                  <span
                    style={{
                      background: 'linear-gradient(90deg, #FF6B00 0%, #FF3D00 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    FORGE
                  </span>
                </div>

                {/* — INSIGHTS — */}
                <div
                  style={{
                    fontFamily: "'Rajdhani', 'Space Grotesk', sans-serif",
                    fontSize: '11px',
                    fontWeight: '800',
                    letterSpacing: '0.35em',
                    color: '#94A3B8',
                    marginTop: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span style={{ width: '18px', height: '1.5px', background: '#00C0FA' }} />
                  INSIGHTS
                  <span style={{ width: '18px', height: '1.5px', background: '#FF5722' }} />
                </div>
              </div>
            </div>

            {/* Value Proposition Description */}
            <p style={{ color: '#94A3B8', fontSize: '13.5px', lineHeight: 1.6, marginBottom: '28px', fontWeight: '500' }}>
              Enterprise intelligence platform unifying corporate reports, real-time OneDrive governance, and automated distribution workflows.
            </p>

            {/* 4 Brand Pillars (AI • Analytics • Automation • Transformation) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {TF_PILLARS.map((p, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '14px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.06)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      flexShrink: 0,
                      border: `1px solid ${p.color}40`,
                    }}
                  >
                    {p.icon}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#FFFFFF', fontWeight: '800', fontSize: '13.5px' }}>{p.title}</span>
                      <span style={{ fontSize: '9.5px', fontWeight: '900', color: p.color, background: `${p.color}20`, padding: '2px 6px', borderRadius: '4px', letterSpacing: '0.06em' }}>
                        {p.badge}
                      </span>
                    </div>
                    <div style={{ color: '#94A3B8', fontSize: '12px', marginTop: '2px', lineHeight: 1.4 }}>
                      {p.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Tagline in Exact Trendforge Typography */}
          <div
            style={{
              marginTop: '32px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontFamily: "'Space Grotesk', 'Rajdhani', sans-serif",
                fontSize: '10.5px',
                fontWeight: '800',
                letterSpacing: '0.12em',
                color: '#38BDF8',
              }}
            >
              AI • ANALYTICS • AUTOMATION • TRANSFORMATION
            </div>
            <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>
              v10.0 Enterprise
            </span>
          </div>
        </div>

        {/* ── RIGHT AUTHENTICATION PANEL ── */}
        <div
          style={{
            padding: '48px 40px',
            background: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* Top Header & 1-Pager Link */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <div
                style={{
                  fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
                  fontSize: '20px',
                  fontWeight: '900',
                  letterSpacing: '0.06em',
                  color: '#0F172A',
                }}
              >
                TREND <span style={{ color: '#FF5722' }}>FORGE</span>
              </div>
              <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#64748B', marginTop: '2px' }}>
                Secure Document Intelligence Portal
              </div>
            </div>

            <Link
              to="/one-pager"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '20px',
                background: '#EEF2FF',
                border: '1px solid #C7D2FE',
                color: '#4F46E5',
                fontWeight: '800',
                fontSize: '11.5px',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              ✨ Sales Sheet →
            </Link>
          </div>

          <div style={{ fontSize: '24px', fontWeight: '900', color: '#0F172A', letterSpacing: '-0.02em', marginBottom: '6px' }}>
            Welcome Back
          </div>
          <div style={{ fontSize: '13.5px', color: '#64748B', marginBottom: '24px' }}>
            Sign in to access your operational reports and dispatches.
          </div>

          {/* KYRO Voice Assistant Login Hint Banner */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: 'linear-gradient(135deg, rgba(0, 192, 250, 0.12) 0%, rgba(11, 19, 43, 0.05) 100%)',
              border: '1px solid rgba(0, 192, 250, 0.35)',
              borderRadius: '12px',
              marginBottom: '20px',
            }}
          >
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: '#00C0FA',
                boxShadow: '0 0 10px #00C0FA',
                flexShrink: 0,
              }}
            />
            <div style={{ fontSize: '11.5px', color: '#1E293B', lineHeight: 1.45 }}>
              <strong style={{ color: '#0070BA' }}>🎙️ KYRO Voice Active:</strong> Click the orb or say{' '}
              <span style={{ color: '#0052CC', fontWeight: '700' }}>"Log in as Admin"</span> or{' '}
              <span style={{ color: '#0052CC', fontWeight: '700' }}>"Log in as Naveen"</span> for hands-free login.
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', marginBottom: '6px' }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 42px',
                    borderRadius: '12px',
                    border: '1.5px solid #CBD5E1',
                    fontSize: '14px',
                    fontWeight: '600',
                    outline: 'none',
                    background: '#F8FAFC',
                    color: '#0F172A',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#0052CC'; e.target.style.background = '#FFFFFF'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#CBD5E1'; e.target.style.background = '#F8FAFC'; }}
                />
                <span style={{ position: 'absolute', left: '14px', top: '13px', fontSize: '16px', color: '#94A3B8' }}>
                  ✉️
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 42px 12px 42px',
                    borderRadius: '12px',
                    border: '1.5px solid #CBD5E1',
                    fontSize: '14px',
                    fontWeight: '600',
                    outline: 'none',
                    background: '#F8FAFC',
                    color: '#0F172A',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#0052CC'; e.target.style.background = '#FFFFFF'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#CBD5E1'; e.target.style.background = '#F8FAFC'; }}
                />
                <span style={{ position: 'absolute', left: '14px', top: '13px', fontSize: '16px', color: '#94A3B8' }}>
                  🔒
                </span>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '12px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#94A3B8' }}
                  title="Toggle password visibility"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {error && (
              <div
                style={{
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  color: '#991B1B',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  textAlign: 'center',
                }}
              >
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #0052CC 0%, #00C0FA 100%)',
                color: '#FFFFFF',
                fontSize: '15px',
                fontWeight: '900',
                letterSpacing: '0.04em',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 8px 20px rgba(0, 82, 204, 0.35)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {loading ? 'Authenticating…' : 'Sign In to PX-Reports →'}
            </button>
          </form>

          {/* Quick Demo Credentials Bar */}
          <div style={{ marginTop: '24px', paddingTop: '18px', borderTop: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
              Quick Demo Login — Click to fill:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => fillDemo(a)}
                  style={{
                    background: '#F8FAFC',
                    border: '1.5px solid #E2E8F0',
                    borderRadius: '10px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#334155',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0052CC'; e.currentTarget.style.background = '#EFF6FF'; e.currentTarget.style.color = '#0052CC'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = '#334155'; }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
